import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Pivot, PivotItem } from '@fluentui/react';

import { RootState } from '../../../redux/application/redux-store';
import { DateTimeEnchanter } from '../../../utils/data-enchanters';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useTicket } from './hooks/useTicket';
import { useClassNames } from './ticket-page.classNames';

import { IAIApplicationLayout } from '../../../common/layouts/IAIApplicationLayout';
import { PIVOTKEYS } from '../../../utils/constants';
import PivotItemContent from './components/PivotItemConditionalContent/pivot-item-content';
import { CustomView, TicketFilteringCriteria } from '../../../api-client';
import { keepCriteria } from '../../../redux/slices/Ticket/ticketSlice';

const TicketPage: React.FunctionComponent = () => {
    const {
        pageContainer
    } = useClassNames();

    const {
        handleTickets,
        tickets,
        ticketsLoading,
        handlePersonalTickets,
        personalTickets,
        personalTicketsLoading,
        handleDeleteTicket,
        deleteTicketLoading,
        handleCustomViewTickets,
        exportTicketsLoading,
        handleTicketExport
    } = useTicket();

    const dispatch = useDispatch();
    const location = useLocation();

    const useQuery = () => {
        return new URLSearchParams(location.search);
    }

    const queryProvider = useQuery();

    const iaiAccount = useSelector((state: RootState) => state.account);
    const iaiCriteria = useSelector((state: RootState) => state.ticket.criteria);
    const iaiSystemCustomViews = useSelector((state: RootState) => state.system.customViews);

    const [lastUpdate, setLastUpdate] = useState<string>();
    const [selectedKey, setSelectedKey] = React.useState<string>(iaiAccount.role !== 'user' ? PIVOTKEYS.ALLTICKETS : PIVOTKEYS.MYTICKETS);

    const _handleDefaultTicket = () => {
        let requestedStatus = queryProvider.get("status");
        if (!!requestedStatus && requestedStatus !== 'all') {
            handleTickets({ criteria: { ...iaiCriteria, statusList: [requestedStatus] } })
        } else {
            handleTickets({ criteria: iaiCriteria });
        }

        handlePersonalTickets({});

        setLastUpdate(new Date().toISOString());
    };

    useEffect(() => {
        _handleDefaultTicket();

        const interval = setInterval(() => {
            _handleDefaultTicket();
        }, 300000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryProvider.get("status")]);

    const _handleDeleteTicket = (id: string, onSuccess: () => void) => {
        handleDeleteTicket({
            id: id,
            onSuccess: () => {
                handleTickets({ criteria: iaiCriteria });
                handlePersonalTickets({});

                onSuccess();
            }
        });
    }

    const _handleCustomViewTickets = (customView: CustomView) => {
        return () => { return handleCustomViewTickets(customView, { ...iaiCriteria }); };
    }

    const _handleTicketExport = () => {
        if (!exportTicketsLoading) {
            handleTicketExport({ criteria: iaiCriteria }, i18n.language);
        }
    }

    const {
        i18n,
        t: translate
    } = useTranslation();

    return (
        <IAIApplicationLayout
            icon={'Ticket'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={`${translate(TRANSLATION_KEYS.SecondaryHeading)} ${DateTimeEnchanter.formatShortTime(lastUpdate, i18n.language)}`}
        >
            <div className={pageContainer}>
                <Pivot aria-label="Tickets pivot" onLinkClick={(item) => {
                    setSelectedKey(item?.props?.itemKey ?? '')
                }}>
                    {iaiAccount.role !== 'user' && (
                        <PivotItem
                            headerText={translate(TRANSLATION_KEYS.TicketPivotHeading)}
                            itemCount={tickets?.length}
                            itemIcon="WorkforceManagement"
                            itemKey={PIVOTKEYS.ALLTICKETS}
                        >
                            <PivotItemContent
                                activeKey={selectedKey}
                                itemKey={PIVOTKEYS.ALLTICKETS}
                                data={tickets || []}
                                dataLoading={ticketsLoading}
                                onApplyCriteria={(filterCriteria: TicketFilteringCriteria | undefined) => {
                                    dispatch(keepCriteria(filterCriteria ?? {}));
                                    handleTickets({ criteria: filterCriteria })
                                }}
                                onClearCriteria={() => handleTickets({})}
                                deleteTicketLoading={deleteTicketLoading}
                                onDeleteTicket={_handleDeleteTicket}
                                onExportTicket={_handleTicketExport}
                            />
                        </PivotItem>
                    )}

                    <PivotItem
                        headerText={translate(TRANSLATION_KEYS.PersonalTicketPivotHeading)}
                        itemCount={personalTickets?.length}
                        itemIcon="AccountManagement"
                        itemKey={PIVOTKEYS.MYTICKETS}
                    >
                        <PivotItemContent
                            activeKey={selectedKey}
                            itemKey={PIVOTKEYS.MYTICKETS}
                            data={personalTickets || []}
                            dataLoading={personalTicketsLoading}
                        />
                    </PivotItem>

                    {iaiAccount.role !== 'user' && iaiSystemCustomViews.map((x) => {
                        const elementKey = `${PIVOTKEYS.CUSTOMIZED}${x.id}`;
                        return (<PivotItem
                            headerText={x.displayName}
                            itemIcon="WorkforceManagement"
                            itemKey={elementKey}
                            key={elementKey}
                        >
                            <PivotItemContent
                                dataLoading={false}
                                activeKey={selectedKey}
                                itemKey={elementKey}
                                handleTicketsRetrieve={_handleCustomViewTickets(x)}
                                deleteTicketLoading={deleteTicketLoading}
                                onDeleteTicket={_handleDeleteTicket}
                                onExportTicket={_handleTicketExport}
                            />
                        </PivotItem>)
                    })
                    }

                </Pivot>
            </div>
        </IAIApplicationLayout>
    );
}

export default TicketPage;